import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  makeStyles,
  Theme,
  Typography,
  Fab,
  Tooltip,
} from '@material-ui/core';

import SaveIcon from '@material-ui/icons/Save';

import moment from 'moment';
import { useHistory, useParams } from 'react-router';
import Input from '../../../components/Input';
import { useAxios } from '../../../utils/hooks';
import getValidationErrors from '../../../utils/getValidationErrors';
import { Form as FormModel } from '../../../models/form';
import { Record } from '../../../models/record';
import Loader from '../../../components/Loader';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';
import Signatures from '../../../components/Signatures';
import { SignatureForm } from '../../../models/signature-form';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    background: 'white',
    padding: theme.spacing(3),
    margin: theme.spacing(3),
    marginBottom: theme.spacing(10),
  },
  formTitles: {
    fontWeight: 'bolder',
  },
  formControl: {
    margin: theme.spacing(2, 0),
  },
  divider: {
    margin: theme.spacing(2),
  },
  floatingButton: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 1000,
  },
}));

const ReviewForm: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const api = useAxios();
  const [form, setForm] = useState<FormModel | null>(null);
  const [record, setRecord] = useState<Record | null>(null);
  const [signatures, setSignatures] = useState<SignatureForm[]>([]);
  const { id, formId } = useParams<{ id: string; formId?: string }>();

  const formRef = useRef<FormHandles>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getRecordData() {
      try {
        const recordResponse = await api.current?.get(`/records/${id}`);
        setRecord(recordResponse?.data);

        if (formId) {
          const formResponse = await api.current?.get(`/forms/${formId}`);
          setForm(formResponse?.data);

          if (
            formResponse?.data.answers &&
            formResponse?.data.answers.signatures
          ) {
            setSignatures(formResponse?.data.answers.signatures);
          }
        }
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar prontuário.',
        });
      } finally {
        setLoading(false);
      }
    }

    getRecordData();
  }, [api, id, formId]);

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleSubmit = useCallback(
    async (data: { [key: string]: unknown }, action = 'submit') => {
      try {
        setLoading(true);
        resetAlert();
        formRef.current?.setErrors({});

        // const schema = Yup.object().shape({
        //   content: Yup.string().required('Insira uma nota'),
        // });

        // await schema.validate(data, { abortEarly: false });

        const formRequest = {
          type: 'review',
          title: 'Ficha de Avaliação',
          patient: record?.patient._id,
          record: record?._id,
          answers: { ...data, signatures },
        };

        if (formId) {
          await api.current?.patch(`/forms/${formId}`, formRequest);
        } else {
          await api.current?.post('/forms', formRequest);
        }

        if (action === 'save') {
          setForm({ ...form, answers: { ...data, signatures } } as FormModel);

          setAlert({
            isActive: true,
            type: 'success',
            message: 'Avaliação salva com sucesso.',
          });

          setLoading(false);
        } else {
          history.push(`/prontuarios/${id}`);
        }
      } catch (error) {
        setLoading(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao enviar avaliação.',
          });
        }
      }
    },
    [api, record, history, formId, id, form, signatures],
  );

  return (
    <>
      <Loader loading={loading} />

      {!loading && (
        <Form
          id="form"
          ref={formRef}
          onSubmit={handleSubmit}
          className={classes.root}
          initialData={form?.answers}
        >
          <FormControl fullWidth className={classes.formControl}>
            <Typography
              variant="h5"
              component="h1"
              align="center"
              className={classes.formTitles}
            >
              FICHA DE AVALIAÇÃO
            </Typography>

            <Divider className={classes.divider} />
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={2}>
              <Input id="age" label="Idade" name="age" />
            </Grid>

            <Grid item xs={3}>
              <Input id="gender" label="Sexo" name="gender" />
            </Grid>

            <Grid item xs={1} />

            <Grid item xs={2}>
              <FormLabel className={classes.formTitles}>
                Período de avaliação:
              </FormLabel>
            </Grid>

            <Grid item xs={2}>
              <Input id="period_start" label="De" name="period_start" />
            </Grid>

            <Grid item xs={2}>
              <Input id="period_end" label="a" name="period_end" />
            </Grid>

            <Grid item xs={6}>
              <Input id="professional" label="Terapeuta" name="professional" />
            </Grid>

            <Grid item xs={6}>
              <Input
                id="supervisor"
                label="Supervisor ou Preceptor"
                name="supervisor"
              />
            </Grid>
          </Grid>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  1 - ENCAMINHAMENTO
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="forward"
                  label="Encaminhamento"
                  name="forward"
                  isMultiline
                  rows={15}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  2 - RESUMO DA HISTÓRIA PREGRESSA
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="summary"
                  label="Resumo da história pregressa"
                  name="summary"
                  isMultiline
                  rows={15}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  3 - IMPRESSÃO INICIAL DO PACIENTE
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="initial_impression"
                  label="Impressão inicial do paciente"
                  name="initial_impression"
                  isMultiline
                  rows={15}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  4 - IMPRESSÃO INICIAL DOS PAIS
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="initial_parent_impression"
                  label="Impressão inicial dos pais"
                  name="initial_parent_impression"
                  isMultiline
                  rows={15}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  5 - LISTA DE SINTOMAS
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="symptoms"
                  label="Lista de sintomas"
                  name="symptoms"
                  isMultiline
                  rows={15}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  6 - HISTÓRIA DE INTERVENÇÕES PRÉVIAS EM SAÚDE MENTAL
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="mental_health_history"
                  label="(Hospitalizações, Psicoterapia, Avaliações Psiquiátricas e Psicológicas)"
                  name="mental_health_history"
                  isMultiline
                  rows={15}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  7 - HISTÓRIA DE ESTRESSES E CRISES RELEVANTES PARA CRIANÇA E
                  FAMÍLIA
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="stress_history"
                  label="(Morte, doenças, separações, problemas econômicos, doença mental)"
                  name="stress_history"
                  isMultiline
                  rows={15}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  8 - RECOMENDAÇÃO PARA AVALIAÇÕES ADICIONAIS
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="extra_evaluation"
                  label="(testagem psicológica, psiquiatra, neurologista, etc.)"
                  name="extra_evaluation"
                  isMultiline
                  rows={15}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  9 - HIPÓTESE DIAGNÓSTICA INICIAL
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="cid"
                  label="(CID 10)"
                  name="cid"
                  isMultiline
                  rows={15}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  10 - PLANO DE TRATAMENTO INICIAL
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="initial_plan"
                  label="(Incluindo tipo de tratamento para criança e pais, outras intervenções e encaminhamento)"
                  name="initial_plan"
                  isMultiline
                  rows={15}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Assinaturas
                </FormLabel>
              </Grid>

              <Grid item xs={12} justify="center">
                <Signatures
                  formAnswers={form ? form.answers : {}}
                  onSignature={setSignatures}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2} justify="flex-end">
              <Grid item xs={2}>
                <Input
                  id="form_data"
                  label="Data"
                  name="form_data"
                  defaultValue={moment().format('DD/MM/YYYY').toString()}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={1} justify="center">
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => history.push(`/prontuarios/${id}`)}
                >
                  Voltar
                </Button>
              </Grid>

              <Grid item>
                <Button type="submit" color="primary" variant="contained">
                  Enviar
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Form>
      )}

      {alert.isActive && <Snackbar {...alert} />}

      {!loading && (
        <div>
          <Tooltip
            title={
              formId
                ? 'Salvar avaliação'
                : 'Envie um primeiro rascunho da ficha'
            }
          >
            <span
              style={{
                display: 'inline-block',
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000,
              }}
            >
              <Fab
                color="primary"
                disabled={!formId}
                aria-label="salvar"
                onClick={() => {
                  if (formId) {
                    handleSubmit(formRef.current?.getData() || {}, 'save');
                  }
                }}
              >
                <SaveIcon />
              </Fab>
            </span>
          </Tooltip>
        </div>
      )}
    </>
  );
};

export default ReviewForm;
